import React, { useEffect, useState } from 'react';
import { Plus, Trash2, ExternalLink, MapPin, Crown, Check, CreditCard } from 'lucide-react';
import { api, formatApiError, setStoredActiveRid } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { Modal } from '../lib/Modal';

const STATUS_COLOR = {
    active: 'var(--neon-teal)',
    trial: 'var(--neon-cyan)',
    pending_payment: 'var(--amber)',
    suspended: 'var(--neon-magenta)',
    expired: 'var(--text-faint)',
};

const STATUS_LABEL = {
    active: 'Activ',
    trial: 'Trial',
    pending_payment: 'Plată în așteptare',
    suspended: 'Suspendat',
    expired: 'Expirat',
};

export default function LocationsPage() {
    const { user, restaurant, myLocations, switchLocation, refreshLocations } = useAuth();
    const [error, setError] = useState('');
    const [deleting, setDeleting] = useState(null);
    const [switchingId, setSwitchingId] = useState(null);

    useEffect(() => {
        refreshLocations?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (user?.role !== 'restaurant_admin') {
        return (
            <div className="panel" style={{ maxWidth: 540, margin: '2rem auto', textAlign: 'center' }}>
                <h2 className="headline" style={{ fontSize: '1.4rem' }}>Acces restricționat</h2>
                <p style={{ color: 'var(--text-muted)' }}>
                    Doar proprietarii contului pot gestiona locații.
                </p>
            </div>
        );
    }

    const activeId = restaurant?.id;
    const handleSwitch = async (id) => {
        if (id === activeId) return;
        setSwitchingId(id);
        try {
            await switchLocation(id);
        } catch (e) {
            setError(formatApiError(e));
            setSwitchingId(null);
        }
    };

    const handleDelete = async (loc) => {
        const confirmation = window.prompt(
            `Ștergi definitiv locația "${loc.name}"?\n\n` +
            `Vor fi șterse toate produsele, mesele, comenzile și ospătarii din această locație.\n` +
            `Această acțiune NU poate fi anulată.\n\n` +
            `Pentru a confirma, scrie numele locației:`,
        );
        if (confirmation !== loc.name) {
            if (confirmation !== null) {
                setError('Numele nu se potrivește. Ștergere anulată.');
            }
            return;
        }
        setDeleting(loc.id);
        setError('');
        try {
            await api.delete(`/r/locations/${loc.id}`);
            await refreshLocations();
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setDeleting(null);
        }
    };

    return (
        <div className="fade-in-up" data-testid="locations-page">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end', marginBottom: '1.4rem', flexWrap: 'wrap', gap: '1rem' }}>
                <div>
                    <div className="kicker">Lanț locații</div>
                    <h1 className="headline" style={{ fontSize: '2.4rem', margin: '0.3rem 0 0' }}>Locațiile mele.</h1>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: 6, maxWidth: 580 }}>
                        Gestionezi toate locațiile lanțului tău dintr-un singur cont. Fiecare locație are propriul plan, abonament,
                        meniu și staff. Comută între ele oricând fără să te re-loghezi.
                    </p>
                </div>
                <AddLocationButton onCreated={async () => { await refreshLocations(); }} />
            </div>

            {error && (
                <div className="panel" style={{
                    marginBottom: '1rem',
                    background: 'rgba(255,46,146,0.08)',
                    border: '1px solid rgba(255,46,146,0.3)',
                    color: 'var(--neon-magenta)',
                }} data-testid="locations-error">{error}</div>
            )}

            {(!myLocations || myLocations.length === 0) ? (
                <div className="panel" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                    Se încarcă locațiile...
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(320px, 100%), 1fr))', gap: '1rem' }}>
                    {myLocations.map((loc) => {
                        const isActive = loc.id === activeId;
                        const isPrimary = loc.is_primary;
                        return (
                            <div
                                key={loc.id}
                                className="panel"
                                data-testid={`location-card-${loc.id}`}
                                style={{
                                    padding: '1.1rem',
                                    border: isActive ? '1px solid var(--amber)' : '1px solid var(--border)',
                                    boxShadow: isActive ? '0 0 0 1px var(--amber) inset, 0 6px 20px rgba(242,101,34,0.18)' : undefined,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 10,
                                }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                            <MapPin size={15} style={{ color: 'var(--amber)' }} />
                                            {isPrimary && (
                                                <span style={{
                                                    fontSize: '0.65rem',
                                                    color: 'var(--amber)',
                                                    background: 'rgba(242,101,34,0.14)',
                                                    padding: '1px 6px',
                                                    borderRadius: 3,
                                                    fontWeight: 700,
                                                    letterSpacing: '0.04em',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 3,
                                                }}><Crown size={9} /> PRIMARY</span>
                                            )}
                                        </div>
                                        <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700 }}>{loc.name}</h3>
                                        <div style={{ color: 'var(--text-faint)', fontSize: '0.78rem', marginTop: 4 }}>{loc.slug}</div>
                                    </div>
                                    {isActive && (
                                        <span className="badge badge-amber" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                            <Check size={11} /> Activ
                                        </span>
                                    )}
                                </div>

                                <div style={{
                                    display: 'flex', gap: 6, flexWrap: 'wrap', fontSize: '0.78rem',
                                    paddingTop: 8, borderTop: '1px solid var(--border)',
                                }}>
                                    <span className={`badge ${loc.plan === 'pro' ? 'badge-amber' : 'badge-teal'}`}>
                                        {(loc.plan || '').toUpperCase()}
                                    </span>
                                    <span style={{
                                        padding: '2px 8px',
                                        borderRadius: 999,
                                        background: 'var(--surface-2)',
                                        color: STATUS_COLOR[loc.status] || 'var(--text-muted)',
                                        fontWeight: 600,
                                    }}>
                                        {STATUS_LABEL[loc.status] || loc.status}
                                    </span>
                                </div>

                                {loc.status === 'pending_payment' && (
                                    <div style={{ fontSize: '0.82rem', color: 'var(--amber)', lineHeight: 1.45 }}>
                                        Locația așteaptă prima plată. Comută aici, apoi mergi în <strong>Setări → Abonament</strong> pentru a plăti.
                                    </div>
                                )}

                                <div style={{ display: 'flex', gap: 6, marginTop: 'auto' }}>
                                    <button
                                        type="button"
                                        className={`btn btn-sm ${isActive ? 'btn-secondary' : 'btn-primary'}`}
                                        onClick={() => handleSwitch(loc.id)}
                                        disabled={isActive || switchingId === loc.id}
                                        style={{ flex: 1 }}
                                        data-testid={`location-switch-${loc.id}`}
                                    >
                                        {switchingId === loc.id
                                            ? <span className="spinner" />
                                            : (isActive ? 'Aici lucrezi' : 'Comută aici')}
                                    </button>
                                    {!isPrimary && (
                                        <button
                                            type="button"
                                            className="btn btn-danger btn-sm"
                                            onClick={() => handleDelete(loc)}
                                            disabled={deleting === loc.id}
                                            title="Șterge locația"
                                            data-testid={`location-delete-${loc.id}`}
                                        >
                                            {deleting === loc.id ? <span className="spinner" /> : <Trash2 size={14} />}
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// Two-step modal: details → payment. After /r/locations creates the new
// restaurant (and switches the active-location cookie to it), the second step
// triggers /r/subscription/checkout which inherits that switched context and
// redirects the owner to the processor's hosted checkout. The user can also
// skip payment and pay later from Settings.
function AddLocationButton({ onCreated }) {
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState('form');    // 'form' | 'pay'
    const [name, setName] = useState('');
    const [plan, setPlan] = useState('pro');
    const [cycle, setCycle] = useState('monthly');
    // Which processor button is mid-checkout (so only its spinner shows). '' = idle.
    const [payingProcessor, setPayingProcessor] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [createdLoc, setCreatedLoc] = useState(null);

    const reset = () => {
        setStep('form');
        setName('');
        setError('');
        setCreatedLoc(null);
        setBusy(false);
    };

    const closeModal = () => {
        setOpen(false);
        // tiny delay so the closing animation doesn't show the form jumping back
        setTimeout(reset, 200);
    };

    const create = async () => {
        setError('');
        if (!name.trim()) {
            setError('Numele locației e obligatoriu.');
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post('/r/locations', {
                name: name.trim(), plan, billing_cycle: cycle,
            });
            setCreatedLoc(data);
            // Backend sets the tf_active_rid cookie but it's SameSite=Lax and
            // won't ride cross-origin XHR. Mirror the same choice into
            // localStorage so the next call (/r/subscription/checkout) carries
            // the X-Restaurant-Id header and targets the freshly-created
            // location instead of the owner's primary.
            if (data?.id) setStoredActiveRid(data.id);
            await onCreated?.(data);
            setStep('pay');
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const pay = async (processor) => {
        if (busy) return;
        setError('');
        setPayingProcessor(processor);
        setBusy(true);
        try {
            const { data } = await api.post('/r/subscription/checkout', {
                plan, billing_cycle: cycle, processor,
            });
            if (data.checkout_url) {
                window.location.href = data.checkout_url;
                return;
            }
            setError('Nu am primit URL de plată — încearcă din Setări → Abonament.');
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setBusy(false);
            setPayingProcessor('');
        }
    };

    const skip = async () => {
        // Refresh locations list and close. New location stays in pending_payment.
        await onCreated?.(createdLoc);
        closeModal();
    };

    return (
        <>
            <button
                type="button"
                className="btn btn-primary"
                onClick={() => { setOpen(true); setError(''); }}
                data-testid="add-location-btn"
            >
                <Plus size={16} /> Adaugă locație nouă
            </button>
            <Modal open={open} title={step === 'pay' ? 'Plătește locația' : 'Locație nouă'} onClose={closeModal}>
                {step === 'form' ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: '0.88rem', lineHeight: 1.5 }}>
                            Creezi o nouă locație sub același cont. Datele de facturare (CUI, firmă) se copiază automat din locația principală.
                            Locația rămâne <strong>fără acces până la prima plată</strong>.
                        </p>
                        <div>
                            <label className="label" htmlFor="loc-name">Nume locație</label>
                            <input id="loc-name" className="input" placeholder="Ex: Panda București"
                                   value={name} onChange={(e) => setName(e.target.value)}
                                   data-testid="add-location-name" />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <div>
                                <label className="label" htmlFor="loc-plan">Plan</label>
                                <select id="loc-plan" className="input" value={plan} onChange={(e) => setPlan(e.target.value)} data-testid="add-location-plan">
                                    <option value="standard">Standard</option>
                                    <option value="pro">Pro</option>
                                </select>
                            </div>
                            <div>
                                <label className="label" htmlFor="loc-cycle">Ciclu plată</label>
                                <select id="loc-cycle" className="input" value={cycle} onChange={(e) => setCycle(e.target.value)} data-testid="add-location-cycle">
                                    <option value="monthly">Lunar</option>
                                    <option value="quarterly">3 luni</option>
                                    <option value="semiannual">6 luni</option>
                                    <option value="annual">12 luni</option>
                                </select>
                            </div>
                        </div>
                        {error && (
                            <div style={{ color: 'var(--neon-magenta)', fontSize: '0.86rem' }} data-testid="add-location-error">{error}</div>
                        )}
                        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                            <button type="button" className="btn btn-secondary" onClick={closeModal} disabled={busy} style={{ flex: 1 }}>
                                Renunță
                            </button>
                            <button type="button" className="btn btn-primary" onClick={create} disabled={busy || !name.trim()} style={{ flex: 1 }} data-testid="add-location-submit">
                                {busy ? <span className="spinner" /> : <>Continuă <span style={{ opacity: 0.7 }}>→</span></>}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <div style={{
                            padding: '0.9rem 1rem',
                            background: 'rgba(20,241,149,0.06)',
                            border: '1px solid rgba(20,241,149,0.3)',
                            borderRadius: 10,
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--neon-teal)', fontWeight: 600, marginBottom: 4 }}>
                                <Check size={16} /> Locația „{createdLoc?.name}" a fost creată
                            </div>
                            <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                                Plan {plan.toUpperCase()} · ciclu {cycle}. Pentru a o activa, plătește acum sau revino mai târziu din <strong>Setări → Abonament</strong>.
                            </p>
                        </div>
                        <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }} data-testid="add-location-processor-info">
                            Alege metoda de plată. Factura SmartBill se emite automat la confirmare.
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <button type="button" className="btn btn-primary" onClick={() => pay('netopia')} disabled={busy}
                                    style={{ justifyContent: 'flex-start', gap: 10 }} data-testid="add-location-pay-netopia">
                                {payingProcessor === 'netopia' ? <span className="spinner" /> : <CreditCard size={16} />}
                                <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.2 }}>
                                    <strong>Card bancar (Netopia)</strong>
                                    <span style={{ fontSize: '0.72rem', opacity: 0.85, fontWeight: 400 }}>Visa, Mastercard</span>
                                </span>
                                <ExternalLink size={14} style={{ marginLeft: 'auto', opacity: 0.7 }} />
                            </button>
                            <button type="button" className="btn btn-primary" onClick={() => pay('revolut')} disabled={busy}
                                    style={{ justifyContent: 'flex-start', gap: 10 }} data-testid="add-location-pay">
                                {payingProcessor === 'revolut' ? <span className="spinner" /> : <CreditCard size={16} />}
                                <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.2 }}>
                                    <strong>Revolut Pay</strong>
                                    <span style={{ fontSize: '0.72rem', opacity: 0.85, fontWeight: 400 }}>Card, Apple Pay, Google Pay</span>
                                </span>
                                <ExternalLink size={14} style={{ marginLeft: 'auto', opacity: 0.7 }} />
                            </button>
                        </div>
                        {error && (
                            <div style={{ color: 'var(--neon-magenta)', fontSize: '0.86rem' }} data-testid="add-location-pay-error">{error}</div>
                        )}
                        <button type="button" className="btn btn-secondary" onClick={skip} disabled={busy} data-testid="add-location-skip">
                            Plătesc mai târziu
                        </button>
                    </div>
                )}
            </Modal>
        </>
    );
}
